import { useState, useEffect } from 'react';
import { DataTable, Button, useToast } from '@/shared/components/ui';
import { Pencil, Trash2 } from 'lucide-react';
import { rolService } from '../api/rolService';
import { RolListaDto } from '@/core/sdk/modules/administracion/rolClient';
import { CreateRolModal } from '../ui/CreateRolModal';
import { EditRolModal } from '../ui/EditRolModal';
import { DeleteConfirmModal } from '../ui/DeleteConfirmModal';
import styles from './RolesPage.module.css';

export const RolesPage = () => {
    const [roles, setRoles] = useState<RolListaDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const { showToast } = useToast();

    // Cargar roles al montar
    useEffect(() => {
        loadRoles();
    }, []);

    const loadRoles = async () => {
        try {
            setLoading(true);
            const data = await rolService.getAll();
            setRoles(data);
        } catch (error: any) {
            showToast({
                type: 'error',
                message: error.message || 'Error al cargar roles'
            });
        } finally {
            setLoading(false);
        }
    };

    // Filtrar datos
    const filteredData = (roles || []).filter(item => {
        const matchesSearch = item.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
        return matchesSearch;
    });

    // Columnas del DataTable
    const columns = [
        {
            key: 'nombre' as keyof RolListaDto,
            header: 'Nombre'
        },
        {
            key: 'descripcion' as keyof RolListaDto,
            header: 'Descripción',
            render: (_: any, row: RolListaDto) => row.descripcion || '-'
        },
        {
            key: 'cantidadUsuarios' as keyof RolListaDto,
            header: 'Usuarios',
            render: (_: any, row: RolListaDto) => row.cantidadUsuarios
        },
        {
            key: 'actions' as keyof RolListaDto,
            header: 'Acciones',
            render: (_: any, row: RolListaDto) => (
                <div className={styles.actions}>
                    <button
                        className={`${styles.iconButton} ${styles.editButton}`}
                        onClick={() => setEditingId(row.id)}
                        title="Editar"
                        aria-label={`Editar rol ${row.nombre}`}
                    >
                        <Pencil size={16} strokeWidth={2.5} />
                    </button>
                    <button
                        className={`${styles.iconButton} ${styles.deleteButton}`}
                        onClick={() => setDeletingId(row.id)}
                        title="Eliminar"
                        aria-label={`Eliminar rol ${row.nombre}`}
                    >
                        <Trash2 size={16} strokeWidth={2.5} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Roles</h1>
                <Button
                    variant="primary"
                    onClick={() => setShowCreateModal(true)}
                >
                    Nuevo Rol
                </Button>
            </div>

            <div className={styles.filters}>
                <input
                    type="text"
                    placeholder="Buscar por nombre o descripción..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                />
            </div>

            <DataTable
                data={filteredData}
                columns={columns}
                loading={loading}
            />

            {/* Modal de Creación */}
            {showCreateModal && (
                <CreateRolModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        loadRoles();
                    }}
                />
            )}

            {/* Modal de Edición */}
            {editingId !== null && (
                <EditRolModal
                    isOpen={editingId !== null}
                    rolId={editingId}
                    onClose={() => setEditingId(null)}
                    onSuccess={() => {
                        setEditingId(null);
                        loadRoles();
                    }}
                />
            )}

            {/* Modal de Eliminación */}
            {deletingId !== null && (
                <DeleteConfirmModal
                    isOpen={deletingId !== null}
                    rolId={deletingId}
                    onClose={() => setDeletingId(null)}
                    onSuccess={() => {
                        setDeletingId(null);
                        loadRoles();
                    }}
                />
            )}
        </div>
    );
};
